(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const nav = document.querySelector('[data-respecto-mobile-bottom-nav]');
  if (!nav) return;

  const items = [...nav.querySelectorAll('[data-rmbn-index]')];
  const pageIndex = Number(nav.dataset.pageIndex);
  const catalogueIndex = 2;
  let pressTimer = 0;
  let navigationLockTimer = 0;
  let navigationLocked = false;
  let skipNextLinkTransition = false;
  let observedPreviewBar = null;
  let previewResizeObserver = null;
  let viewportFrame = 0;
  let catalogueDetailsObserver = null;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  function setActive(index, options = {}) {
    const numericIndex = Number(index);
    const hasActive = Number.isInteger(numericIndex) && numericIndex >= 0 && numericIndex < items.length;

    nav.dataset.activeIndex = hasActive ? String(numericIndex) : '-1';

    items.forEach((item) => {
      const active = hasActive && Number(item.dataset.rmbnIndex) === numericIndex;
      item.classList.toggle('is-active', active);
      if (options.manageCurrent) {
        if (active && item.tagName === 'A') item.setAttribute('aria-current', 'page');
        else if (item.tagName === 'A') item.removeAttribute('aria-current');
      }
    });
  }

  function showPress(item) {
    window.clearTimeout(pressTimer);
    items.forEach((candidate) => candidate.classList.remove('rmbn-is-pressed'));
    item.classList.add('rmbn-is-pressed');
    pressTimer = window.setTimeout(() => item.classList.remove('rmbn-is-pressed'), 320);
  }

  function resetNavigationLock() {
    window.clearTimeout(navigationLockTimer);
    navigationLockTimer = 0;
    navigationLocked = false;
  }

  function catalogueIsOpen() {
    const details = document.getElementById('Details-menu-drawer-container');
    return details?.hasAttribute('open')
      && details.closest('header-drawer')?.classList.contains('rmbn-open-from-catalogue');
  }

  function syncCatalogueState() {
    setActive(catalogueIsOpen() ? catalogueIndex : pageIndex);
  }

  function isVisiblyOpen(element) {
    if (!(element instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && rect.width > 0
      && rect.height > 0;
  }

  function dialogIsOpen() {
    return [...document.querySelectorAll('dialog[open]')].some(isVisiblyOpen);
  }

  function headerDrawerIsOpen() {
    return [...document.querySelectorAll('#header-group details[open], #header-group header-drawer details[open]')]
      .some(isVisiblyOpen);
  }

  function keyboardIsOpen() {
    const viewport = window.visualViewport;
    if (!viewport || !document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return false;
    return window.innerHeight - viewport.height > 120;
  }

  function syncVisibility() {
    const shouldHide = !isMobile() || headerDrawerIsOpen() || dialogIsOpen() || keyboardIsOpen();
    nav.classList.toggle('rmbn-is-hidden', shouldHide);
    nav.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
    if ('inert' in nav) nav.inert = shouldHide || catalogueIsOpen();
  }

  function syncPreviewOffset() {
    const previewBar = document.getElementById('PBarNextFrameWrapper');
    const height = previewBar && isVisiblyOpen(previewBar)
      ? Math.ceil(previewBar.getBoundingClientRect().height)
      : 0;
    document.body.style.setProperty('--rmbn-preview-offset', `${height}px`);
    syncCatalogueOrigin();

    if (previewBar && previewBar !== observedPreviewBar && 'ResizeObserver' in window) {
      previewResizeObserver?.disconnect();
      observedPreviewBar = previewBar;
      previewResizeObserver = new ResizeObserver(syncPreviewOffset);
      previewResizeObserver.observe(previewBar);
    }
  }

  function syncCatalogueOrigin() {
    if (!isMobile()) return;
    const icon = nav.querySelector(`[data-rmbn-index="${catalogueIndex}"] .rmbn__icon`);
    const drawer = document.querySelector('#header-group header-drawer .menu-drawer');
    if (!icon || !drawer) return;

    const rect = icon.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    drawer.style.setProperty('--rmbn-catalogue-origin-x', `${Math.round(rect.left + rect.width / 2)}px`);
    drawer.style.setProperty('--rmbn-catalogue-origin-y', `${Math.round(rect.top + rect.height / 2)}px`);
  }

  function openCatalogueDrawer(trigger) {
    const headerDrawer = document.querySelector('#header-group header-drawer');
    const details = headerDrawer?.querySelector('#Details-menu-drawer-container');
    const drawer = details?.querySelector('.menu-drawer');
    if (!isMobile() || !headerDrawer || !details || !drawer || typeof headerDrawer.open !== 'function') return false;

    syncCatalogueOrigin();
    headerDrawer.classList.add('rmbn-open-from-catalogue');
    document.body.classList.add('respecto-mobile-catalogue-open');
    trigger.setAttribute('aria-expanded', 'true');
    drawer.scrollTop = 0;

    catalogueDetailsObserver?.disconnect();
    let catalogueReachedOpenState = false;
    catalogueDetailsObserver = new MutationObserver(() => {
      const isStillOpen = details.hasAttribute('open');
      const hasOpenClass = details.classList.contains('menu-open');

      if (isStillOpen && hasOpenClass) {
        catalogueReachedOpenState = true;
        return;
      }

      const isClosing = isStillOpen && catalogueReachedOpenState && !hasOpenClass;

      if (isClosing) {
        nav.classList.add('rmbn-catalogue-closing');
        return;
      }

      if (isStillOpen) return;
      headerDrawer.classList.remove('rmbn-open-from-catalogue');
      document.body.classList.remove('respecto-mobile-catalogue-open');
      trigger.setAttribute('aria-expanded', 'false');
      catalogueDetailsObserver?.disconnect();
      catalogueDetailsObserver = null;
      syncCatalogueState();
      syncVisibility();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => nav.classList.remove('rmbn-catalogue-closing'));
      });
      if (trigger.isConnected && isMobile()) {
        window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
      }
    });
    catalogueDetailsObserver.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });

    details.setAttribute('open', '');
    headerDrawer.open();
    return true;
  }

  function scheduleViewportSync({ preview = false } = {}) {
    if (viewportFrame) return;
    viewportFrame = window.requestAnimationFrame(() => {
      viewportFrame = 0;
      if (preview) syncPreviewOffset();
      else {
        syncVisibility();
        syncCatalogueOrigin();
      }
    });
  }

  nav.addEventListener('pointerdown', (event) => {
    if (!isMobile() || !(event.target instanceof Element)) return;
    const item = event.target.closest('[data-rmbn-index]');
    if (!item) return;
    showPress(item);
    const targetIndex = Number(item.dataset.rmbnIndex);
    if (targetIndex === catalogueIndex) {
      syncCatalogueOrigin();
      setActive(catalogueIndex);
    }
  }, { passive: true });

  nav.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const catalogueTrigger = event.target.closest('[data-respecto-mobile-catalogue-trigger]');
    if (catalogueTrigger && isMobile()) {
      event.preventDefault();
      event.stopPropagation();
      if (!catalogueIsOpen()) openCatalogueDrawer(catalogueTrigger);
      return;
    }

    const item = event.target.closest('a[data-rmbn-index]');
    if (!item || !isMobile() || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const targetUrl = new URL(item.href, window.location.href);
    const currentUrl = new URL(window.location.href);
    const isCurrentPage = targetUrl.origin === currentUrl.origin
      && targetUrl.pathname === currentUrl.pathname
      && targetUrl.search === currentUrl.search;

    if (isCurrentPage || navigationLocked) {
      event.preventDefault();
      return;
    }

    navigationLocked = true;
    skipNextLinkTransition = true;
    navigationLockTimer = window.setTimeout(resetNavigationLock, 900);
  });

  window.addEventListener('pageswap', (event) => {
    if (!skipNextLinkTransition) return;
    try {
      event.viewTransition?.skipTransition();
    } catch (_error) {
      // The theme may already have skipped this transition.
    }
    skipNextLinkTransition = false;
  });
  window.addEventListener('pageshow', resetNavigationLock);

  const bodyObserver = new MutationObserver(() => {
    syncCatalogueState();
    syncVisibility();
  });
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  const interfaceObserver = new MutationObserver(syncVisibility);
  interfaceObserver.observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });

  const previewBarObserver = new MutationObserver(syncPreviewOffset);
  previewBarObserver.observe(document.body, { childList: true });

  window.visualViewport?.addEventListener('resize', () => scheduleViewportSync(), { passive: true });
  window.addEventListener('orientationchange', () => {
    syncVisibility();
    window.requestAnimationFrame(syncCatalogueOrigin);
  }, { passive: true });
  window.addEventListener('resize', () => scheduleViewportSync({ preview: true }), { passive: true });

  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  mobileMedia.addEventListener?.('change', syncVisibility);
  mobileMedia.addListener?.(syncVisibility);

  syncCatalogueState();
  syncPreviewOffset();
  syncCatalogueOrigin();
  syncVisibility();
})();
